import React, { useEffect, useState, type JSX } from 'react';
import { X } from 'lucide-react';
import { PortaledLayer } from './PortaledLayer';
import CyclingLoader from './CyclingLoader';
import DeviceDetails from '../Device/DeviceDetails';
import type { DeviceMasterRow, DeviceStatus } from '../../../types/DeviceListTypes';
import {
  normalizeDeviceMappingRows,
  resolveDeviceMappingId,
} from '../../helpers/deviceMappingRows';

type Props = {
  deviceId: string | null;
  operatorScopedEventId?: string | null;
  onClose: () => void;
};

export function DeviceRosterOverlay({ deviceId, operatorScopedEventId = null, onClose }: Props): JSX.Element | null {
  const [row, setRow] = useState<DeviceMasterRow | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [status, setStatus] = useState<DeviceStatus | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deviceId) {
      setRow(null);
      setColumns([]);
      setStatus(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRow(null);
    setStatus(undefined);

    async function load() {
      try {
        const [masterRes, statusRes] = await Promise.all([
          window.eventsAPI?.getDeviceMappings?.(),
          window.eventsAPI?.getDeviceStatus?.(deviceId!),
        ]);
        if (cancelled) return;

        if (masterRes?.ok && Array.isArray(masterRes?.data?.devices)) {
          const { rows: devices } = normalizeDeviceMappingRows(masterRes.data.devices);
          const found = devices.find((d) => resolveDeviceMappingId(d) === deviceId);
          if (found) {
            setRow(found);
            setColumns(Object.keys(found));
          }
        }

        const statusData = statusRes?.data as { status?: DeviceStatus } | undefined;
        if (statusRes?.ok && statusData?.status) {
          setStatus(statusData.status);
        }
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error)?.message ?? 'Failed to load device');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [deviceId, onClose]);

  if (!deviceId) return null;

  return (
    <PortaledLayer>
      <div
        className="fixed inset-0 z-[5300] flex items-center justify-center bg-black/40 p-4 dark:bg-black/70"
        data-race-stacked-overlay=""
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-roster-overlay-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="flex max-h-[min(80vh,720px)] w-full max-w-[min(1400px,96vw)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <h2
              id="device-roster-overlay-title"
              className="text-sm font-semibold text-gray-900 dark:text-slate-100"
            >
              Device details
              <span className="ml-2 font-mono font-normal text-gray-500 dark:text-slate-400">{deviceId}</span>
            </h2>
            <button
              type="button"
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              onClick={onClose}
              aria-label="Close device details"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-3 dark:bg-slate-900">
            {loading ? (
              <div className="flex justify-center py-8">
                <CyclingLoader phrases={['Loading device details…']} size="sm" />
              </div>
            ) : error ? (
              <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">{error}</p>
            ) : row ? (
              <DeviceDetails
                meta={row}
                metaColumns={columns}
                operatorScopedEventId={operatorScopedEventId}
                status={status}
              />
            ) : (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-slate-400">
                Device <span className="font-mono">{deviceId}</span> not found in device mappings.
              </p>
            )}
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
